import React from "react";
import { Link } from "react-router-dom";
import { LegalShell, Section, ResponsiveTable } from "../components/LegalShell";

const LAST_UPDATED = "26 February 2026";

export default function Privacy() {
    return (
        <LegalShell
            eyebrow="Legal"
            title="Privacy Policy"
            subtitle="How EuroLaw Hub processes personal data, the sub-processors actually used, the Article 6 GDPR legal bases per purpose, retention periods, your rights, and how to exercise them."
            lastUpdated={LAST_UPDATED}
        >
            <Section title="1. Who we are">
                <p>
                    EuroLaw Hub (the "Service") is operated by EuroLaw Hub. For the
                    purposes of this Policy, EuroLaw Hub acts as data
                    <strong> controller</strong> for the personal data described
                    here, except where we explicitly process data on a customer's
                    behalf (e.g. documents you upload), in which case we act as{" "}
                    <strong>processor</strong> under your instructions.
                </p>
                <p>
                    Contact and DPO:{" "}
                    <a className="underline" href="mailto:altay@eurolawhub.com">
                        altay@eurolawhub.com
                    </a>
                </p>
            </Section>

            <Section title="2. Categories of personal data we process">
                <ResponsiveTable
                    testId="privacy-categories"
                    columns={[
                        { key: "category", label: "Category" },
                        { key: "fields", label: "Fields" },
                        { key: "source", label: "Source" },
                    ]}
                    rows={[
                        { category: "Account", fields: "Email, name, hashed password, plan tier, role, must-reset-password flag", source: "You, at sign-up" },
                        { category: "Sessions", fields: "Session identifier, last-login timestamp, IP and user agent on login", source: "Generated by the platform" },
                        { category: "Conversations", fields: "Your prompts and the model's replies to you", source: "You, in use" },
                        { category: "Documents", fields: "PDFs, DOCX and TXT you upload + derived text chunks and vector embeddings", source: "You, in use" },
                        { category: "Watches and alerts", fields: "Topics you track, alerts generated for you, read/dismissed state", source: "You + generated by the platform" },
                        { category: "Billing", fields: "Plan key, subscription status, payment references from our payment processor", source: "You + our payment processor" },
                        { category: "Support", fields: "Support-chat transcripts, email correspondence to altay@eurolawhub.com", source: "You" },
                        { category: "Consent", fields: "Consent record (categories accepted, timestamp, version, IP, user agent)", source: "Generated when you interact with the cookie banner" },
                        { category: "Analytics (if enabled)", fields: "Aggregated, non-identifying page views and feature-usage counts", source: "Only if you enable analytics cookies" },
                    ]}
                />
            </Section>

            <Section title="3. Article 6 GDPR legal bases per purpose">
                <ResponsiveTable
                    testId="privacy-legal-bases"
                    columns={[
                        { key: "purpose", label: "Purpose" },
                        { key: "data", label: "Data used" },
                        { key: "basis", label: "Legal basis" },
                    ]}
                    rows={[
                        { purpose: "Operating the Service (auth, sessions, research, uploads, workspaces)", data: "Account, sessions, conversations, documents, watches", basis: "Art. 6(1)(b), performance of a contract" },
                        { purpose: "Securing the Service (abuse prevention, audit logs)", data: "IP, user agent, route, timestamp", basis: "Art. 6(1)(f), legitimate interest in keeping the service safe" },
                        { purpose: "Billing", data: "Plan, payment IDs", basis: "Art. 6(1)(b), performance of a contract; Art. 6(1)(c), accounting and tax obligations" },
                        { purpose: "Customer support", data: "Email, support-chat transcripts", basis: "Art. 6(1)(b) and Art. 6(1)(f)" },
                        { purpose: "Optional analytics and preferences cookies", data: "Aggregated usage signals", basis: "Art. 6(1)(a), your consent" },
                        { purpose: "Marketing (if you opt in)", data: "Email, basic profile", basis: "Art. 6(1)(a), your consent" },
                        { purpose: "Legal claims and regulatory cooperation", data: "Any of the above as strictly necessary", basis: "Art. 6(1)(c) and Art. 6(1)(f)" },
                    ]}
                />
            </Section>

            <Section title="4. Sub-processors (the ones we actually use)">
                <p>
                    To deliver the Service we engage the following sub-processors.
                    Each is bound by a Data Processing Addendum and, where transfers
                    leave the EEA, by Standard Contractual Clauses or equivalent
                    safeguards under Article 46 GDPR.
                </p>
                <ResponsiveTable
                    testId="privacy-subprocessors"
                    columns={[
                        { key: "name", label: "Sub-processor" },
                        { key: "purpose", label: "Purpose" },
                        { key: "location", label: "Location" },
                        { key: "safeguard", label: "Transfer safeguard" },
                    ]}
                    rows={[
                        { name: "Anthropic", purpose: "AI inference (Claude). NO TRAINING on customer content per the DPA.", location: "United States", safeguard: "DPA + Standard Contractual Clauses (Art. 46)" },
                        { name: "Payment processor (merchant of record)", purpose: "Subscription billing, invoicing, tax compliance", location: "United States / EU", safeguard: "DPA + SCCs; the processor is the merchant of record for EU VAT" },
                        { name: "Managed database hosting (EU region)", purpose: "Application database (users, conversations, documents, alerts, bulletins)", location: "European Union", safeguard: "Intra-EEA processing under the GDPR" },
                        { name: "Email-delivery provider", purpose: "Transactional email (password reset, billing receipts, support correspondence)", location: "EU / EEA", safeguard: "Intra-EEA where possible; SCCs otherwise" },
                    ]}
                />
                <p>
                    We update this list when a sub-processor changes. Material
                    changes will be notified by email or in-app banner.
                </p>
                <p data-testid="privacy-internal-access">
                    <strong>Internal access.</strong> Account records (email, name,
                    plan and verification/trial status, team seat allocation,
                    sign-up date and last activity) are viewable by the platform
                    administrator strictly for account management, billing support
                    and abuse prevention. Administrator access to account listings
                    and exports is itself recorded in our audit log.
                </p>
            </Section>

            <Section title="5. International transfers">
                <p>
                    The primary database is hosted in the European Union. The only
                    routine transfer outside the EEA today is to Anthropic for AI
                    inference. That transfer is covered by Anthropic's DPA and
                    Standard Contractual Clauses, supplemented by a documented
                    Transfer Impact Assessment under EDPB Recommendations 01/2020.
                    Anthropic does not train on customer content.
                </p>
            </Section>

            <Section title="6. Retention">
                <ResponsiveTable
                    testId="privacy-retention"
                    columns={[
                        { key: "category", label: "Category" },
                        { key: "retention", label: "Retention" },
                    ]}
                    rows={[
                        { category: "Account record", retention: "For the life of your account, plus up to 90 days after deletion to honour reversal requests; permanently anonymised thereafter." },
                        { category: "Conversations", retention: "Until you delete them, or 24 months after your last login if your account remains inactive." },
                        { category: "Documents and embeddings", retention: "Until you delete them. Hard-deletion within 30 days." },
                        { category: "Watches and alerts", retention: "Until you dismiss them; dismissed alerts soft-archive after 6 months." },
                        { category: "Billing and tax records", retention: "Up to 10 years where required by EU/Member-State tax law." },
                        { category: "Security and audit logs", retention: "12 months." },
                        { category: "Consent records", retention: "5 years from the date of the consent decision, for accountability under Art. 7(1) GDPR." },
                        { category: "Aggregated analytics (if enabled)", retention: "Aggregated indefinitely; no individual-level identifiers." },
                    ]}
                />
            </Section>

            <Section title="7. Your rights (Articles 15–22 GDPR)">
                <ResponsiveTable
                    testId="privacy-rights"
                    columns={[
                        { key: "right", label: "Right" },
                        { key: "art", label: "Article" },
                        { key: "how", label: "How to exercise" },
                    ]}
                    rows={[
                        { right: "Access", art: "Art. 15", how: "Email altay@eurolawhub.com; we respond within 30 days." },
                        { right: "Rectification", art: "Art. 16", how: "Update directly in /app/profile, or email us for fields you cannot edit." },
                        { right: "Erasure ('right to be forgotten')", art: "Art. 17", how: "Delete your account from /app/profile, or email altay@eurolawhub.com. Documents can be deleted individually from the Research Assistant." },
                        { right: "Restriction", art: "Art. 18", how: "Email altay@eurolawhub.com." },
                        { right: "Portability", art: "Art. 20", how: "Email altay@eurolawhub.com, we provide a structured JSON/CSV export." },
                        { right: "Object", art: "Art. 21", how: "Email altay@eurolawhub.com. We stop unless we can demonstrate compelling legitimate grounds." },
                        { right: "Not be subject to a solely-automated decision", art: "Art. 22", how: "EuroLaw Hub does not take legal or significant decisions about you solely on the basis of automated processing." },
                        { right: "Withdraw consent", art: "Art. 7(3)", how: "Use the Cookie Preferences link in the footer, or email us for consents given over email." },
                    ]}
                />
                <p className="mt-3">
                    You also have the right to lodge a complaint with the
                    supervisory authority in your Member State of habitual
                    residence, place of work or place of the alleged infringement
                    (Article 77 GDPR).
                </p>
            </Section>

            <Section title="8. Cookies and similar technologies">
                <p>
                    We use cookies and equivalent browser storage only where
                    permitted. Strictly-necessary items load by default; everything
                    else loads only after you consent. You can review and change
                    your choices at any time via the{" "}
                    <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent("elh-consent-open"))}
                        className="underline"
                        data-testid="privacy-open-cookie-prefs"
                    >
                        Cookie Preferences
                    </button>{" "}
                    link in the footer.
                </p>
            </Section>

            <Section title="9. Changes to this Policy">
                <p>
                    We will update this Policy as the platform evolves. The "Last
                    updated" date at the top reflects the current version. Material
                    changes trigger a fresh consent request and an in-app banner.
                </p>
            </Section>

            <Section title="10. Account deletion and erasure (Article 17 GDPR)">
                <p>
                    You can delete your account at any time from{" "}
                    <strong>Account → Delete account</strong>. Deletion is a two-step,
                    explicitly confirmed action. When you confirm:
                </p>
                <ul className="list-disc pl-5 space-y-2 mt-3">
                    <li>
                        Your account is <strong>deactivated immediately</strong> and you
                        are signed out. Any active subscription is cancelled with our
                        payment provider. No further charges are made.
                    </li>
                    <li>
                        A <strong>30-day grace period</strong> begins. During this window
                        you may restore the account (with all saved work intact) simply by
                        signing back in and confirming restoration.
                    </li>
                    <li>
                        After the grace period, all personal data (profile, uploaded
                        documents, research conversations, watchdogs, alerts, usage
                        records and billing records) is{" "}
                        <strong>permanently and irreversibly erased</strong> by an
                        automated purge job, in accordance with Article 17 GDPR (right to
                        erasure).
                    </li>
                    <li>
                        Audit-log entries are anonymised rather than deleted, to satisfy
                        our accountability obligations; they no longer reference you.
                    </li>
                    <li>
                        Invoices and tax records held by our merchant-of-record payment
                        partner are retained by them for the statutory retention period
                        required by tax law (Art. 17(3)(b) GDPR).
                    </li>
                </ul>
            </Section>
        </LegalShell>
    );
}
